/* eslint-disable @eslint-react/no-children-to-array -- Need to handle children as array */

'use client';

import {
  FunctionComponent,
  useState,
  useMemo,
  type PropsWithChildren,
  Children,
  isValidElement
} from 'react';
import { classes } from '@/next-utils/css-utils/scss-utils';
import LoggerService from '@/services/isomorphic/LoggerService';
import S from './styles.base.module.scss';

/**
 * The ways that the component can be triggered to swap content.
 */
export enum SwappableComponentTrigger {
  Click = 'click',
  Hover = 'hover',
  Touch = 'touch'
}

export interface ISwappableComponentProps extends PropsWithChildren {
  /**
   * The trigger configurations that determine how the component swaps content.
   * Each trigger defines a type of user interaction (click, hover, touch).
   */
  triggers: Array<{
    /**
     * The type of trigger that activates the content swap.
     * This can be 'click', 'hover', or 'touch'.
     */
    type: SwappableComponentTrigger;
  }>;
}

/**
 * A swappable component that can display different content variants
 * and swap between states on interaction. The component expects exactly
 * two children elements - the first is always visible, the second is
 * shown/hidden based on the swapped state.
 *
 * @param children - The child elements to swap between (expects exactly 2).
 * @param triggers - Array of trigger configurations that determine how swapping is activated.
 * @returns A swappable container with two child elements.
 */
export const SwappableComponent: FunctionComponent<
  ISwappableComponentProps
> = ({ children, triggers }) => {
  // Ensure that children are valid React elements
  const blocks = Children.toArray(children).filter((child) =>
    isValidElement(child)
  );

  const topElement = useMemo(() => {
    if (blocks.length === 0) {
      LoggerService.warn(
        'SwappableComponent expects at least one child element. None provided.'
      );
      return null; // Ensure we have at least one block
    }
    return blocks.at(0); // Ensure we have at least one block
  }, [blocks]);

  const bottomElement = useMemo(() => {
    if (blocks.length < 2) {
      LoggerService.warn(
        'SwappableComponent expects exactly two children elements. Only one provided.'
      );
      return null; // Ensure we have at least two blocks
    }
    return blocks.at(1); // Ensure we have at least two blocks
  }, [blocks]);

  const [swapped, setSwapped] = useState(false);

  if (blocks.length === 0) {
    LoggerService.warn(
      'SwappableComponent expects at least one child element. None provided.'
    );
    return null;
  }

  return (
    <div
      className={S.swappableContainer}
      role="button"
      tabIndex={0}
      aria-pressed={swapped}
      onClick={() => {
        if (
          triggers.some(
            (trigger) => trigger.type === SwappableComponentTrigger.Click
          )
        ) {
          setSwapped(!swapped);
        }
      }}
      onKeyDown={(e) => {
        if (
          triggers.some(
            (trigger) => trigger.type === SwappableComponentTrigger.Click
          ) &&
          (e.key === 'Enter' || e.key === ' ')
        ) {
          e.preventDefault();
          setSwapped(!swapped);
        }
      }}
      onMouseEnter={() => {
        if (
          triggers.some(
            (trigger) => trigger.type === SwappableComponentTrigger.Hover
          )
        ) {
          setSwapped(true);
        }
      }}
      onMouseLeave={() => {
        if (
          triggers.some(
            (trigger) => trigger.type === SwappableComponentTrigger.Hover
          )
        ) {
          setSwapped(false);
        }
      }}
      onTouchStart={() => {
        if (
          triggers.some(
            (trigger) => trigger.type === SwappableComponentTrigger.Touch
          )
        ) {
          setSwapped(!swapped);
        }
      }}
    >
      <div className={classes(S.swappableItem, { [S.visible]: swapped })}>
        {bottomElement}
      </div>
      <div className={classes(S.swappableItem, { [S.visible]: !swapped })}>
        {topElement}
      </div>
    </div>
  );
};

export default SwappableComponent;
